import {
  DropdownMenuArrow as DropdownMenuArrowPrimitive,
  DropdownMenuContent as DropdownMenuContentPrimitive,
  DropdownMenuItemIndicator as DropdownMenuItemIndicatorPrimitive,
  DropdownMenuItem as DropdownMenuItemPrimitive,
  DropdownMenuPortal,
  DropdownMenuRadioItem as DropdownMenuRadioItemPrimitive,
  DropdownMenuSeparator as DropdownMenuSeparatorPrimitive,
  type DropdownMenuContentProps as DropdownMenuContentPrimitiveProps,
  type DropdownMenuItemIndicatorProps as DropdownMenuItemIndicatorPrimitiveProps,
  type DropdownMenuItemProps as DropdownMenuItemPrimitiveProps,
  type DropdownMenuPortalProps,
  type DropdownMenuRadioItemProps as DropdownMenuRadioItemPrimitiveProps,
  type DropdownMenuSeparatorProps as DropdownMenuSeparatorPrimitiveProps,
} from '../../primitives';
import { cn, cva, type VariantProps } from '../../shared';
import { isPassthrough } from './interact-outside';

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
  type DropdownMenuCheckboxItemProps,
  type DropdownMenuGroupProps,
  type DropdownMenuLabelProps,
  type DropdownMenuProps,
  type DropdownMenuRadioGroupProps,
  type DropdownMenuTriggerProps,
} from '../../primitives';

// Surface/border/shadow match PopoverContent, but padding uses `px-dropdown-item-x
// py-dropdown-item-y` instead of `p-card` — the same gutter `Select`'s own menu list uses, since
// items here already carry their own padding and `p-card` on top of that reads as too much air.
export const dropdownMenuContentVariants = cva([
  'bg-popover text-popover-foreground border-border z-popover rounded-lg border shadow-overlay',
  'px-dropdown-item-x py-dropdown-item-y',
  'outline-none',
  'min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-panel',
  'origin-[var(--radix-dropdown-menu-content-transform-origin)]',
  'data-[state=open]:animate-zoom-in',
  'data-[state=closed]:animate-fade-out',
]);

interface DropdownMenuContentBase extends Omit<
  DropdownMenuContentPrimitiveProps,
  'aria-label' | 'aria-labelledby'
> {
  arrow?: boolean;
  container?: DropdownMenuPortalProps['container'];
}

/** Radix supplies `role="menu"` and no name, so the type requires one. */
export type DropdownMenuContentProps =
  | (DropdownMenuContentBase & { 'aria-label': string; 'aria-labelledby'?: never })
  | (DropdownMenuContentBase & { 'aria-labelledby': string; 'aria-label'?: never });

export function DropdownMenuContent({
  className,
  arrow = false,
  container,
  sideOffset = 6,
  align = 'start',
  onInteractOutside,
  children,
  ...props
}: DropdownMenuContentProps) {
  return (
    <DropdownMenuPortal {...(container === undefined ? {} : { container })}>
      <DropdownMenuContentPrimitive
        sideOffset={sideOffset}
        align={align}
        onInteractOutside={(event) => {
          if (isPassthrough(event.target)) event.preventDefault();
          onInteractOutside?.(event);
        }}
        className={cn(dropdownMenuContentVariants(), className)}
        {...props}
      >
        {children}
        {arrow ? (
          <DropdownMenuArrowPrimitive
            className="fill-popover stroke-border"
            width={12}
            height={6}
          />
        ) : null}
      </DropdownMenuContentPrimitive>
    </DropdownMenuPortal>
  );
}

// Shared with `dropdownMenuRadioItemVariants` below, which only adds a trailing indicator slot.
const DROPDOWN_MENU_ITEM_BASE = [
  'gap-inline px-dropdown-item-x py-dropdown-item-y text-body rounded-md',
  'flex cursor-pointer items-center outline-none select-none',
  'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
] as const;

export const dropdownMenuItemVariants = cva([...DROPDOWN_MENU_ITEM_BASE]);

type DropdownMenuItemVariants = VariantProps<typeof dropdownMenuItemVariants>;

export interface DropdownMenuItemProps
  extends DropdownMenuItemPrimitiveProps, DropdownMenuItemVariants {}

export function DropdownMenuItem({ className, ...props }: DropdownMenuItemProps) {
  return (
    <DropdownMenuItemPrimitive className={cn(dropdownMenuItemVariants(), className)} {...props} />
  );
}

// Negative horizontal margin so the rule reaches the popover's own edges: the content carries
// `px-dropdown-item-x`, and a divider inset by that padding reads as a stray line inside a list
// rather than as the boundary between two groups of items.
export const dropdownMenuSeparatorVariants = cva(
  '-mx-dropdown-item-x my-dropdown-item-y bg-border h-px',
);

export type DropdownMenuSeparatorProps = DropdownMenuSeparatorPrimitiveProps;

export function DropdownMenuSeparator({ className, ...props }: DropdownMenuSeparatorProps) {
  return (
    <DropdownMenuSeparatorPrimitive
      className={cn(dropdownMenuSeparatorVariants(), className)}
      {...props}
    />
  );
}

// `justify-between`, not a margin on the indicator — the item already stretches full width.
export const dropdownMenuRadioItemVariants = cva([...DROPDOWN_MENU_ITEM_BASE, 'justify-between']);

export type DropdownMenuRadioItemProps = DropdownMenuRadioItemPrimitiveProps;

export function DropdownMenuRadioItem({ className, ...props }: DropdownMenuRadioItemProps) {
  return (
    <DropdownMenuRadioItemPrimitive
      className={cn(dropdownMenuRadioItemVariants(), className)}
      {...props}
    />
  );
}

export type DropdownMenuItemIndicatorProps = DropdownMenuItemIndicatorPrimitiveProps;

/** Renders as a plain dot unless given children — every current use is "this option is selected". */
export function DropdownMenuItemIndicator({ children, ...props }: DropdownMenuItemIndicatorProps) {
  return (
    <DropdownMenuItemIndicatorPrimitive {...props}>
      {children ?? <span aria-hidden="true" className="size-2 rounded-full bg-primary" />}
    </DropdownMenuItemIndicatorPrimitive>
  );
}
